import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Button,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { Grain } from '@mui/icons-material';
import { RootState } from '../../store/store';
import {
  addWaypoints,
  setPlannerError,
} from '../../store/slices/missionPlannerSlice';
import {
  defaultSweepAngleDeg,
  lineSpacingFromCamera,
  surveyPath,
} from '../../utils/survey';

/** How the operator specifies the gap between sweep lines. */
type SpacingMode = 'camera' | 'manual';

// Defaults: a typical small-quad wide lens, and the side overlap normally asked
// for by photogrammetry pipelines.
const DEFAULT_FOV_DEG = 84;
const DEFAULT_OVERLAP_PERCENT = 70;
const DEFAULT_MANUAL_SPACING_M = 25;

/** Parse a numeric text field, returning NaN for blank/garbage input. */
const num = (text: string): number => (text.trim() === '' ? NaN : Number(text));

/**
 * Survey-path generation for the selected coverage area (issue #347). Lays a
 * lawnmower sweep over the shape and appends it to the draft as ordinary
 * waypoints — reviewable, editable and undoable, never auto-committed.
 *
 * Line spacing is either derived from the draft altitude plus the camera's
 * field of view and desired overlap, or entered directly in metres. The sweep
 * angle defaults to the shape's longest axis (fewest turns) and is editable.
 */
export const SurveyPathControls: React.FC = () => {
  const dispatch = useDispatch();
  const shapes = useSelector(
    (state: RootState) => state.missionPlanner.draft.shapes
  );
  const selectedShapeId = useSelector(
    (state: RootState) => state.missionPlanner.selectedShapeId
  );
  const altitudeM = useSelector(
    (state: RootState) => state.missionPlanner.draft.profile.altitudeM
  );

  const [spacingMode, setSpacingMode] = useState<SpacingMode>('camera');
  const [fovDeg, setFovDeg] = useState(String(DEFAULT_FOV_DEG));
  const [overlapPercent, setOverlapPercent] = useState(
    String(DEFAULT_OVERLAP_PERCENT)
  );
  const [manualSpacingM, setManualSpacingM] = useState(
    String(DEFAULT_MANUAL_SPACING_M)
  );
  // null = follow the shape's own longest axis; a string = operator override.
  const [angleOverride, setAngleOverride] = useState<string | null>(null);

  const shape = shapes.find((s) => s.id === selectedShapeId) ?? null;

  // A different area has its own natural sweep direction — drop the override.
  useEffect(() => {
    setAngleOverride(null);
  }, [selectedShapeId]);

  if (shapes.length === 0) return null;

  if (!shape) {
    return (
      <Typography variant="body2" color="text.secondary">
        Select a coverage area to generate a survey path over it.
      </Typography>
    );
  }

  const spacingM =
    spacingMode === 'camera'
      ? lineSpacingFromCamera({
          altitudeM,
          fovDeg: num(fovDeg),
          overlapPercent: num(overlapPercent),
        })
      : num(manualSpacingM);

  const sweepAngleDeg =
    angleOverride === null
      ? defaultSweepAngleDeg(shape)
      : num(angleOverride);

  const generate = () => {
    try {
      const path = surveyPath(shape, { lineSpacingM: spacingM, sweepAngleDeg });
      if (path.length === 0) {
        throw new Error(
          'Survey produced no waypoints — the area is too small for this line spacing.'
        );
      }
      dispatch(addWaypoints(path.map(([lat, lng]) => ({ lat, lng }))));
      dispatch(setPlannerError(null));
    } catch (err) {
      dispatch(
        setPlannerError(
          err instanceof Error ? err.message : 'Could not generate a survey path.'
        )
      );
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Typography variant="subtitle2" component="h3">
        Survey path
      </Typography>

      <ToggleButtonGroup
        size="small"
        exclusive
        value={spacingMode}
        onChange={(_e, value: SpacingMode | null) =>
          value && setSpacingMode(value)
        }
        aria-label="Line spacing mode"
      >
        <ToggleButton value="camera" sx={{ textTransform: 'none', minHeight: 44 }}>
          From camera
        </ToggleButton>
        <ToggleButton value="manual" sx={{ textTransform: 'none', minHeight: 44 }}>
          Manual
        </ToggleButton>
      </ToggleButtonGroup>

      <Box sx={{ display: 'flex', gap: 1 }}>
        {spacingMode === 'camera' ? (
          <>
            <TextField
              size="small"
              type="number"
              label="Camera FOV (°)"
              value={fovDeg}
              onChange={(e) => setFovDeg(e.target.value)}
              inputProps={{ min: 1, max: 179, step: 1 }}
              fullWidth
            />
            <TextField
              size="small"
              type="number"
              label="Overlap (%)"
              value={overlapPercent}
              onChange={(e) => setOverlapPercent(e.target.value)}
              inputProps={{ min: 0, max: 95, step: 5 }}
              fullWidth
            />
          </>
        ) : (
          <TextField
            size="small"
            type="number"
            label="Line spacing (m)"
            value={manualSpacingM}
            onChange={(e) => setManualSpacingM(e.target.value)}
            inputProps={{ min: 1, step: 1 }}
            fullWidth
          />
        )}
        <TextField
          size="small"
          type="number"
          label="Sweep angle (°)"
          value={angleOverride ?? String(Math.round(sweepAngleDeg))}
          onChange={(e) => setAngleOverride(e.target.value)}
          inputProps={{ step: 5 }}
          fullWidth
        />
      </Box>

      {spacingMode === 'camera' && (
        <Typography variant="caption" color="text.secondary">
          {Number.isFinite(spacingM) && spacingM > 0
            ? `Line spacing ${spacingM.toFixed(1)} m at ${altitudeM} m altitude.`
            : 'Enter a field of view below 180° and an overlap below 100%.'}
        </Typography>
      )}

      <Button
        variant="outlined"
        startIcon={<Grain />}
        onClick={generate}
        fullWidth
      >
        Generate survey path
      </Button>
    </Box>
  );
};
